import { useState, useEffect, useRef } from 'react';
import type { AxiosInstance } from 'axios';

declare global {
  interface Window {
    axiosInstance?: AxiosInstance;
  }
}

interface UserData {
  [key: string]: unknown;
}

interface AuthData {
  token: string | null;
  role: string | null;
  user: UserData | null;
  isLoggedIn: boolean;
}

interface UseTabAuthReturn extends AuthData {
  login: (token: string, role: string, user: UserData) => void;
  logout: () => void;
  tabId: string;
}

export const useTabAuth = (): UseTabAuthReturn => {
  const [authData, setAuthData] = useState<AuthData>({
    token: null,
    role: null,
    user: null,
    isLoggedIn: false
  });

  const tabIdRef = useRef<string>(`tab_${Date.now()}_${Math.random()}`);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const tabId = tabIdRef.current;
    sessionStorage.setItem('currentTabId', tabId);
    const tabAuthData = sessionStorage.getItem(`tabAuth_${tabId}`);
    if (tabAuthData) {
      try {
        const parsed = JSON.parse(tabAuthData) as AuthData;
        setAuthData(parsed);
      } catch (error) {
        console.error('Failed to parse tab auth data:', error);
      }
    } else {
      const sharedToken = localStorage.getItem('access_token');
      const sharedRole = localStorage.getItem('role');
      const sharedUser = localStorage.getItem('user');

      if (sharedToken && sharedRole && sharedUser) {
        try {
          const userData = JSON.parse(sharedUser) as UserData;
          const authData: AuthData = {
            token: sharedToken,
            role: sharedRole,
            user: userData,
            isLoggedIn: true
          };
          setAuthData(authData);
          sessionStorage.setItem(`tabAuth_${tabId}`, JSON.stringify(authData));
        } catch (error) {
          console.error('Failed to parse shared user data:', error);
        }
      }
    }

    const handleBeforeUnload = (): void => {
      sessionStorage.removeItem(`tabAuth_${tabId}`);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    const checkAuthChanges = (): void => {
      const sharedToken = localStorage.getItem('access_token');
      const sharedRole = localStorage.getItem('role');
      const sharedUser = localStorage.getItem('user');

      if (sharedToken && sharedRole && sharedUser) {
        try {
          const userData = JSON.parse(sharedUser) as UserData;
          const newAuthData: AuthData = {
            token: sharedToken,
            role: sharedRole,
            user: userData,
            isLoggedIn: true
          };

          if (JSON.stringify(newAuthData) !== JSON.stringify(authData)) {
            setAuthData(newAuthData);
            sessionStorage.setItem(`tabAuth_${tabId}`, JSON.stringify(newAuthData));
          }
        } catch (error) {
          console.error('Failed to parse user data:', error);
        }
      } else if (authData.isLoggedIn) {
        setAuthData({
          token: null,
          role: null,
          user: null,
          isLoggedIn: false
        });
        sessionStorage.removeItem(`tabAuth_${tabId}`);
      }
    };

    intervalRef.current = setInterval(checkAuthChanges, 2000);

    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [authData]);

  const login = (token: string, role: string, user: UserData): void => {
    const newAuthData: AuthData = {
      token,
      role,
      user,
      isLoggedIn: true
    };

    setAuthData(newAuthData);

    localStorage.setItem('access_token', token);
    localStorage.setItem('role', role);
    localStorage.setItem('user', JSON.stringify(user));

    const tabId = tabIdRef.current;
    sessionStorage.setItem(`tabAuth_${tabId}`, JSON.stringify(newAuthData));

    if (window.axiosInstance) {
      window.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  };

  const logout = (): void => {
    setAuthData({
      token: null,
      role: null,
      user: null,
      isLoggedIn: false
    });

    localStorage.removeItem('access_token');
    localStorage.removeItem('role');
    localStorage.removeItem('user');

    const tabId = tabIdRef.current;
    sessionStorage.removeItem(`tabAuth_${tabId}`);

    if (window.axiosInstance) {
      delete window.axiosInstance.defaults.headers.common['Authorization'];
    }
  };

  return {
    ...authData,
    login,
    logout,
    tabId: tabIdRef.current
  };
};
